import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { CampaignManagementService } from '../../../services/campaignManagementService';

const CampaignFilters = ({ onFiltersChange, className = '' }) => {
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [comparisonMode, setComparisonMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('last30days');
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusOptions = [
    { value: 'all', label: 'All Campaigns' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'draft', label: 'Draft' }
  ];

  const dateRangeOptions = [
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Load campaigns from Supabase for filter options
  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data: campaignsData, error } = await CampaignManagementService?.getCampaigns();
      
      if (error) {
        console.log('Failed to load campaigns for filters:', error);
        setCampaignOptions([]);
      } else {
        const options = (campaignsData || [])?.map(campaign => ({
          value: campaign?.id,
          label: campaign?.name || 'Unnamed Campaign'
        }));
        setCampaignOptions(options);
      }
    } catch (error) {
      console.log('Error loading campaigns for filters:', error);
      setCampaignOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersUpdate = () => {
    const filters = {
      campaigns: selectedCampaigns,
      status: statusFilter,
      comparison: comparisonMode,
      search: searchQuery,
      dateRange: dateRange
    };
    
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  };

  React.useEffect(() => {
    handleFiltersUpdate();
  }, [selectedCampaigns, statusFilter, comparisonMode, searchQuery, dateRange]);

  const clearAllFilters = () => {
    setSelectedCampaigns([]);
    setStatusFilter('all');
    setComparisonMode(false);
    setSearchQuery('');
    setDateRange('last30days');
  };

  return (
    <div className={`bg-card border-b border-border p-6 ${className}`}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Left Section - Main Filters */}
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Campaign Multi-Select */}
          <div className="min-w-0 flex-1 lg:max-w-xs">
            {loading ? (
              <div className="px-3 py-2 border border-border rounded-md bg-muted text-muted-foreground text-sm">
                Loading campaigns...
              </div>
            ) : campaignOptions?.length > 0 ? (
              <Select
                label="Select Campaigns"
                options={campaignOptions}
                value={selectedCampaigns}
                onChange={setSelectedCampaigns}
                multiple
                searchable
                clearable
                placeholder="Choose campaigns..."
              />
            ) : (
              <div className="px-3 py-2 border border-border rounded-md bg-muted text-muted-foreground text-sm">
                No campaigns available
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="min-w-0 sm:w-40">
            <Select
              label="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </div>

          {/* Date Range */}
          <div className="min-w-0 sm:w-40">
            <Select
              label="Period"
              options={dateRangeOptions}
              value={dateRange}
              onChange={setDateRange}
            />
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="min-w-0 sm:w-64">
            <Input
              type="search"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Comparison Mode Toggle */}
          <Button
            variant={comparisonMode ? 'default' : 'outline'}
            size="default"
            iconName="GitCompare"
            iconPosition="left"
            iconSize={16}
            onClick={() => setComparisonMode(!comparisonMode)}
            className="whitespace-nowrap"
          >
            Compare Mode
          </Button>

          {/* Clear Filters */}
          <Button
            variant="ghost"
            size="default"
            iconName="X"
            iconPosition="left"
            iconSize={16}
            onClick={clearAllFilters}
            className="whitespace-nowrap"
          >
            Clear All
          </Button>
        </div>
      </div>
      {/* Active Filters Display */}
      {(selectedCampaigns?.length > 0 || statusFilter !== 'all' || searchQuery || comparisonMode) && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Active filters:</span>
            
            {selectedCampaigns?.length > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
                <Icon name="Target" size={14} />
                <span>{selectedCampaigns?.length} campaign{selectedCampaigns?.length > 1 ? 's' : ''}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-4 h-4 p-0 hover:bg-primary/20"
                  onClick={() => setSelectedCampaigns([])}
                >
                  <Icon name="X" size={12} />
                </Button>
              </div>
            )}

            {statusFilter !== 'all' && (
              <div className="flex items-center gap-1 px-2 py-1 bg-secondary/10 text-secondary rounded-md text-sm">
                <Icon name="Filter" size={14} />
                <span>{statusOptions?.find(opt => opt?.value === statusFilter)?.label}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-4 h-4 p-0 hover:bg-secondary/20"
                  onClick={() => setStatusFilter('all')}
                >
                  <Icon name="X" size={12} />
                </Button>
              </div>
            )}

            {searchQuery && (
              <div className="flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent rounded-md text-sm">
                <Icon name="Search" size={14} />
                <span>"{searchQuery}"</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-4 h-4 p-0 hover:bg-accent/20"
                  onClick={() => setSearchQuery('')}
                >
                  <Icon name="X" size={12} />
                </Button>
              </div>
            )}

            {comparisonMode && (
              <div className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-md text-sm">
                <Icon name="GitCompare" size={14} />
                <span>Comparison Mode</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-4 h-4 p-0 hover:bg-success/20"
                  onClick={() => setComparisonMode(false)}
                >
                  <Icon name="X" size={12} />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignFilters;